import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import type { FormEvent } from 'react';

import { useWallParticipantsStore, useWallStore, type WallBoard } from '@walls/store';
import {
  getPanelColor,
  getPanelTitleColor,
  getYellowActionColor,
  getYellowActionTextColor,
} from '../theme/wallThemeStyles';

interface WorkspaceJoinPanelProps {
  board: WallBoard;
  topicKey?: string | null;
}

export function WorkspaceJoinPanel({ board, topicKey }: WorkspaceJoinPanelProps) {
  const joinName = useWallParticipantsStore((state) => state.joinName);
  const joinError = useWallParticipantsStore((state) => state.joinError);
  const pendingRejoinName = useWallParticipantsStore((state) => state.pendingRejoinName);
  const isJoining = useWallParticipantsStore((state) => state.isJoining);
  const setJoinName = useWallParticipantsStore((state) => state.setJoinName);
  const joinBoard = useWallParticipantsStore((state) => state.joinBoard);
  const theme = useWallStore((state) => state.theme);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void joinBoard(board, { topicKey });
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={0}
      sx={{
        borderRadius: '5px',
        bgcolor: getPanelColor(theme),
        boxShadow: '0px 2px 5px #c3c3c3',
        p: '15px',
        position: 'fixed',
        right: { xs: 10, sm: 20 },
        top: { xs: 10, sm: 20 },
        zIndex: 1000,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Typography variant="subtitle2" sx={{ color: getPanelTitleColor(theme), minWidth: 190 }}>
          Enter your name to participate
        </Typography>
        <TextField
          size="small"
          label="Name"
          value={joinName}
          onChange={(event) => setJoinName(event.target.value)}
          error={Boolean(joinError && !pendingRejoinName)}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={isJoining}
          sx={{
            bgcolor: getYellowActionColor(theme),
            color: getYellowActionTextColor(theme),
            '&:hover': { bgcolor: getYellowActionColor(theme) },
          }}
        >
          {pendingRejoinName ? 'Rejoin' : 'Join'}
        </Button>
      </Stack>
      {joinError && (
        <Alert severity={pendingRejoinName ? 'info' : 'error'} sx={{ mt: 2 }}>
          {joinError}
        </Alert>
      )}
    </Paper>
  );
}
